import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import { deleteSimulation } from '../../../actions'
import { Logger, LoggerTypes } from '../../../utils'
import { Button, buttonModes } from '../../../components'

export const SimulationActionDialogDelete = forwardRef(
  ({ currentProject, currentScenario, simulation, openDialog, setOpenDialog }, ref) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()

    const onClickCancel = (e) => {
      e.preventDefault()
      e.stopPropagation()

      Logger.trace(LoggerTypes.CONTROLLER, 'SimulationActionDialogDelete.onClickCancel()')
      setOpenDialog(!openDialog)
    }

    const onClickDelete = (e) => {
      e.preventDefault()
      e.stopPropagation()

      const pid = currentProject?.id || null
      const sid = currentScenario?.id || null

      Logger.trace(LoggerTypes.CONTROLLER, 'SimulationActionDialogDelete.onCloseClick()', { simulation })

      dispatch(deleteSimulation(pid, sid, simulation))
        .then((res) => {
          Logger.trace(
            LoggerTypes.CONTROLLER,
            'SimulationActionDialogDelete.onClickDelete().dispatch(deleteSimulation(...)).then()',
            {
              simulation,
              res
            }
          )

          if (res?.data?.isAxiosError) {
            enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
          }

          setOpenDialog(!openDialog)
        })
        .catch((res) => {
          Logger.trace(
            LoggerTypes.WARN,
            'SimulationActionDialogDelete.onClickDelete().dispatch(deleteSimulation(...)).catch()',
            { res }
          )

          const error = (res && res.response && res.response.data && JSON.stringify(res.response.data)) || ''
          enqueueSnackbar(`${t('common.api_failure')} ${error}`, { variant: 'error' })
        })
    }

    Logger.trace(LoggerTypes.CONTAINER, 'SimulationActionDialogDelete.render()', {
      currentProject,
      currentScenario,
      openDialog,
      simulation
    })

    return (
      <Dialog ref={ref} open={openDialog} onClose={onClickCancel} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">{t('simulations.dialogs.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('simulations.dialogs.delete.description')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            mode={buttonModes.SECONDARY_ACTION}
            text={t('common.cancel')}
            icon={<CloseIcon />}
            onClick={onClickCancel}
          />
          <Button
            mode={buttonModes.PRIMARY_ACTION}
            text={t('common.delete')}
            icon={<DeleteOutlineIcon />}
            onClick={onClickDelete}
          />
        </DialogActions>
      </Dialog>
    )
  }
)

SimulationActionDialogDelete.displayName = 'SimulationActionDialogDelete'

SimulationActionDialogDelete.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  simulation: PropTypes.object.isRequired,
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired
}

SimulationActionDialogDelete.defaultProps = {
  currentProject: null,
  currentScenario: null,
  simulation: {},
  openDialog: false,
  setOpenDialog: () => {}
}
